import { ConditionParams } from "@multiweb/clients/dataReader";
import { CancellationToken } from "@multiweb/types/multiweb";
import { InvalidArgumentException, InvalidOperationException } from "../../../Global/Base/Exceptions";
import { OWManager } from "../OWManager";
import { RequestContext } from "../RequestContext";
import { IOWObject } from "./IOWObject";
import { IOWObjectList, IOWObjectListItem } from "./IOWObjectList";
import { OWObject } from "./OWObject";
import { HasPositiveID } from "./utils";

export class OWObjectListItem implements IOWObjectListItem {
    //#region Fields
    /**ObjectID*/
    public readonly id: string | null;

    private _obj: OWObject | null = null;
    /**
     * Promise probíhajícího getu objektu
     */
    private _getPromise: Promise<OWObject> | null;

    /**ObjectName*/
    public readonly objectName: string;
    public readonly instanceID: string | null;
    //#endregion Fields

    //#region Properties
    public get isObjectLoaded(): boolean {
        return this._obj != null;
    }
    //#endregion Properties

    /**
     * Získá instanci objektu
     * Pokud ještě není načtený, načte se podle instanceID nebo ID (podle toho, co je k dispozici)
     * Pokud již probíhá get tohoto objektu, vrací se promise dotazu, aby nedocházelo k duplicitním getům
     * při rychlém volání za sebou.
     * @param requestContext Kontext s rolemi pro dočtení objektu.
     */
    public async getObject(requestContext?: RequestContext): Promise<OWObject> {
        if (this._getPromise != null) {
            return await this._getPromise;
        }

        if (this._obj == null && this.instanceID != null) {
            this._getPromise = OWManager.getInstance(this.instanceID, undefined, undefined, requestContext);
            this._obj = await this._getPromise;
            this._getPromise = null;
        }

        if (this._obj == null && this.id != null && this.objectName) {
            this._getPromise = OWManager.getObject(this.id, this.objectName, undefined, undefined, undefined, requestContext);
            this._obj = await this._getPromise;
            this._getPromise = null;
        }

        return this._obj;
    }

    public compare(item: OWObjectListItem): boolean {
        if (this.objectName != item.objectName)
            return false;

        if (this.instanceID)
            return this.instanceID === item.instanceID;

        if (this.id != null)
            return this.id === item.id;

        throw new InvalidOperationException("OWObjectList item has not id nor instanceid");
    }

    //#region Constructors
    constructor(objIndentification: OWObject);
    constructor(objIndentification: { id?: string; instanceId?: string }, objectName?: string);
    constructor(objIndentification: any, objectName?: any) {
        //Nove pridany zaznam uzivatelem
        if (objIndentification instanceof IOWObject) {
            this._obj = (objIndentification as OWObject);
            this.id = !this._obj.isNew ? (HasPositiveID(this._obj?.id) ? null : this._obj.id) : null;
            this.objectName = objIndentification.objectName;
            this.instanceID = objIndentification.instanceID;
            return;
        }

        if (typeof objIndentification === "object") {
            const obj = objIndentification as { id?: string | number; instanceId?: string };
            this.id = obj.id ? obj.id.toString() : null;
            this.instanceID = obj.instanceId;
            this.objectName = objectName;
            return;
        }

        throw new InvalidArgumentException();
    }

    //#endregion Constructors
}

export class OWObjectList implements IOWObjectList {
    //#region Fields
    private _condition: string;
    private _getRequestContext: (cancellationToken?: CancellationToken) => RequestContext | undefined;
    public updateCondition: string = "";
    public filterCondition: string = "";
    public className: string = "";
    public readVersioned: boolean | undefined = false;

    public get condition(): string {
        let cond = "";

        if (this._condition)
            cond = this._condition;

        if (this.filterCondition) {
            if (cond)
                cond += " and ";
            cond += this.filterCondition;
        }

        if (this.updateCondition) {
            if (cond)
                cond += " and ";
            cond += this.updateCondition;
        }

        return cond;
    }

    public set condition(v: string) {
        this._condition = v;
    }

    public conditionParams: ConditionParams = [];
    public items: Array<OWObjectListItem>;
    public isSelectAll: boolean = false;
    //#endregion Fields

    //#region Constructors

    constructor(params?: IOWObjectListRemote, getRequestContext: (cancellationToken?: CancellationToken) => RequestContext = () => undefined) {
        this.items = new Array<OWObjectListItem>();
        this._getRequestContext = getRequestContext;

        if (params != null) {
            this.className = params.ClassName;
            this.condition = params.Condition;

            this.conditionParams = params.ConditionParams?.map(item => {
                if (item instanceof IOWObjectListRemote) {
                    return new OWObjectList(item, getRequestContext);
                } else {
                    return item;
                }
            });

            this.isSelectAll = params.IsSelectAll;
            this.readVersioned = params.ReadVersioned || false;
            if (params.Items && params.Items.length > 0) {
                for (const item of params.Items) {
                    this.add({ className: item.Name, id: item.ID == null ? null : item.ID, instanceId: item.InstanceID });
                }
            }
        }
    }

    public valueOf(): this {
        return this;
    }
    public toString(): string {
        return `[${OWObjectList.name} on class ${this.className}]`; //`
    }

    //#endregion Constructors

    //#region Methods

    public add(param: { className?: string; id?: string; instanceId?: string }): void;
    public add(param: OWObject): void;
    public add(param: any): void {
        if (param instanceof IOWObject) {
            if (this.containsInstanceID(param.instanceID))
                throw new InvalidArgumentException(`ObjectList already contains item with instanceId ${param.instanceID}`); //`

            this.items.push(new OWObjectListItem(param));
            return;
        }

        if (!param.instanceId && !param.className)
            throw new InvalidArgumentException("className must be specified when adding item by id");

        if (param.instanceId && this.containsInstanceID(param.instanceId))
            throw new InvalidArgumentException(`ObjectList already contains item with instanceId ${param.instanceId}`); //`

        this.items.push(new OWObjectListItem({ id: param.id, instanceId: param.instanceId }, param.className));
    }

    public get count(): number {
        return this.items.length;
    }

    public delete(index: number): void {
        this.items.splice(index, 1);
    }

    public clear(): void {
        this.items = [];
    }

    public async deleteById(id: string): Promise<void> {
        const index = this.indexOf(id);
        if (index == -1) {
            for (let i = 0; i < this.items.length; i++) {
                const actObj = await this.items[i].getObject(this._getRequestContext());

                if (actObj.id === id) {
                    this.items.splice(i, 1);
                    return;
                }
            }
        } else {
            this.items.splice(index, 1);
            return;
        }
    }

    public async deleteByInstanceID(instanceID: string): Promise<void> {
        for (let i = 0; i < this.items.length; i++) {
            const actObj = await this.items[i].getObject(this._getRequestContext());
            if (actObj.instanceID === instanceID) {
                this.items.splice(i, 1);
                return;
            }
        }
    }

    /**vraci ID objektu na dane pozici*/
    public getItemID(index: number): string {
        return this.items[index].id;
    }

    /**vraci ObjectName/ClassName objektu na dane pozici*/
    public getItemObjectName(index: number): string {
        return this.items[index].objectName;
    }

    /**Vrací instance id objektu s daným indexem*/
    public getItemInstanceID(index: number): string {
        return this.items[index].instanceID;
    }

    public async getItemByInstanceID(instanceID: string): Promise<OWObject> {
        for (let i = 0; i < this.items.length; i++) {
            const actObj = await this.items[i].getObject(this._getRequestContext());
            if (actObj.instanceID === instanceID) {
                return actObj;
            }
        }
        return null;
    }

    public async getItemByID(id: string): Promise<OWObject> {
        for (let i = 0; i < this.items.length; i++) {
            const actObj = await this.items[i].getObject(this._getRequestContext());
            if (actObj.id == id) {
                return actObj;
            }
        }
        return null;
    }

    /**vraci objekt na dane pozici*/
    public getObject(index: number): Promise<OWObject> {
        return OWManager.getObject(this.items[index].id, this.items[index].objectName, undefined, undefined, undefined, this._getRequestContext());
    }

    /** Vrací pole všech objektu v OWObjectListu */
    public getObjects(cancellationToken?: CancellationToken): Promise<OWObject[]> {
        const objs: any[] = [];

        for (const item of this.items) {
            objs.push(item.getObject(this._getRequestContext(cancellationToken)));
        }

        return Promise.all(objs);
    }

    /**
     * Vrací, zda v kolekci existuje objekt s daným instanceID
     */
    public containsInstanceID(instanceID: string): boolean {
        return this.items.some((item) => item.instanceID === instanceID);
    }

    /**
     * Vrací, zda v kolekci existuje objekt s daným ID
     */
    public containsID(id: string): boolean {
        return this.items.some((item) => item.id === id);
    }

    /**vraci pozici objektu s danym ID, pokud nenalezne, tak -1*/
    public indexOf(id: string): number {
        let index = 0;
        for (const item of this.items) {
            if (item.id === id)
                return index;
            index++;
        }

        return -1;
    }

    public toJSON(): any {
        const jsonData: any = {
            IsSelectAll: this.isSelectAll,
            ClassName: this.className,
            Count: this.count,
            IsForClient: false,
            ReadVersioned: this.readVersioned,
            Condition: this.condition,
            ConditionParams: this.conditionParams,
            Items: this.items.map((item) => {
                return {
                    ID: item.id,
                    InstanceID: item.instanceID,
                    Name: item.objectName,
                };
            }),
        };

        return jsonData;
    }

    /** Porovná, zda objectlisty mají stejné conditionParams. */
    private _compareConditionParams(objList: OWObjectList): boolean {
        if (this.conditionParams.length !== objList.conditionParams.length)
            return false;

        return this.conditionParams.every((item, i) => {
            if (item instanceof IOWObjectList) {
                return item.compare(objList.conditionParams[i]);
            } else {
                return item === objList.conditionParams[i];
            }
        });
    }

    public compare(value: any): boolean {
        if (value instanceof OWObjectList) {
            if (this.items.length != value.items.length) return false;
            if (!(this.className == value.className && this.condition == value.condition && this._compareConditionParams(value) && this.isSelectAll == value.isSelectAll)) return false;

            const v1Map = this.items;
            const v2Map = value.items;
            const compareObj = (obj: {id: string; instanceID: string; objectName: string}, obj2: {id: string; instanceID: string; objectName: string}) => obj.id == obj2.id && obj.instanceID == obj2.instanceID && obj.objectName == obj2.objectName;

            for (const item of v1Map) {
                for (let i = 0; i < v2Map.length; i++) {
                    const item2 = v2Map[i];
                    if (compareObj(item, item2)) {
                        v2Map.splice(i, 1);
                        break;
                    }
                }
            }

            return v2Map.length == 0;
        }

        return false;
    }

    public clone(): OWObjectList {
        const clone = new OWObjectList(undefined, this._getRequestContext);
        clone.className = this.className;
        clone.condition = this.condition;
        clone.conditionParams = this.conditionParams;
        clone.isSelectAll = this.isSelectAll;
        clone.readVersioned = this.readVersioned;
        clone.items = this.items.map(item => new OWObjectListItem(item));
        return clone;
    }

    //#endregion Methods
}

//export interface IOWObjectListParams { owClass: string, condition: string, conditionParams: string }

export interface IOWObjectListRemote {
    Condition?: string;
    ConditionParams?: Array<any>;
    ClassName?: string;
    Count: number;
    IsForClient: boolean;
    IsSelectAll: boolean;
    Items: Array<IOWObjectListItemRemote>;
    /**Indikuje, že se pro výběr dat pro naplnění objectListu, mají použít verzovaná data */
    ReadVersioned?: boolean;
}

export class IOWObjectListRemote {
    private static _properties = [
        "Count", "IsSelectAll", "Items",
    ];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && typeof instance === "object" && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}

export interface IOWObjectListItemRemote {
    ID: string;
    Name: string;
    InstanceID: string;
}
